/**
 * Utilitaires pour le formatage de masques d'input
 * Supporte les numéros de téléphone canadiens et les codes postaux canadiens
 */

/**
 * Formate un numéro de téléphone canadien au format (999) 999-9999
 * @param value - La valeur à formater (peut contenir des caractères non numériques)
 * @returns La valeur formatée ou une chaîne vide
 */
export function formatPhoneCA(value: string): string {
  // Extraire uniquement les chiffres
  const digits = value.replace(/\D/g, '');

  // Limiter à 10 chiffres
  const limitedDigits = digits.slice(0, 10);

  // Formater selon le nombre de chiffres
  if (limitedDigits.length === 0) {
    return '';
  } else if (limitedDigits.length <= 3) {
    return `(${limitedDigits}`;
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  } else {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  }
}

/**
 * Extrait uniquement les chiffres d'un numéro de téléphone formaté
 * @param value - La valeur formatée
 * @returns Les chiffres uniquement
 */
export function unmaskPhone(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Formate un code postal canadien au format A1A 1A1
 * @param value - La valeur à formater
 * @returns La valeur formatée
 */
export function formatPostalCodeCA(value: string): string {
  // Extraire uniquement les lettres et chiffres, convertir en majuscules
  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  if (cleaned.length === 0) {
    return '';
  }

  // Construire le format A1A 1A1 en acceptant seulement les caractères valides pour chaque position
  let formatted = '';
  
  // Séparer les lettres et les chiffres
  const letters: string[] = [];
  const digits: string[] = [];
  
  for (const char of cleaned) {
    if (/[A-Z]/.test(char)) {
      letters.push(char);
    } else if (/[0-9]/.test(char)) {
      digits.push(char);
    }
  }
  
  // Position 1 : Lettre
  if (letters.length > 0) {
    formatted += letters[0];
  }
  
  // Position 2 : Chiffre
  if (digits.length > 0) {
    formatted += digits[0];
  }
  
  // Position 3 : Lettre
  if (letters.length > 1) {
    formatted += letters[1];
  }
  
  // Espace après le 3ème caractère si on a au moins 4 caractères
  if (letters.length + digits.length > 3) {
    formatted += ' ';
  }
  
  // Position 4 : Chiffre
  if (digits.length > 1) {
    formatted += digits[1];
  }
  
  // Position 5 : Lettre
  if (letters.length > 2) {
    formatted += letters[2];
  }
  
  // Position 6 : Chiffre
  if (digits.length > 2) {
    formatted += digits[2];
  }

  return formatted;
}

/**
 * Extrait les caractères alphanumériques d'un code postal formaté
 * @param value - La valeur formatée
 * @returns Les caractères alphanumériques uniquement (en majuscules)
 */
export function unmaskPostalCode(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/**
 * Valide si un caractère peut être inséré à une position donnée dans un masque
 * @param char - Le caractère à valider
 * @param position - La position dans le masque (0-indexed)
 * @param maskType - Le type de masque ('phone' ou 'postalCodeCA')
 * @returns true si le caractère est valide pour cette position
 */
export function isValidCharForPosition(char: string, position: number, maskType: 'phone' | 'postalCodeCA'): boolean {
  if (maskType === 'phone') {
    // Pour le téléphone, toutes les positions acceptent uniquement des chiffres
    return /[0-9]/.test(char);
  } else if (maskType === 'postalCodeCA') {
    // Positions 0, 2, 4 : lettres (A-Z, a-z)
    // Positions 1, 3, 5 : chiffres (0-9)
    if (position === 0 || position === 2 || position === 4) {
      return /[A-Za-z]/.test(char);
    } else if (position === 1 || position === 3 || position === 5) {
      return /[0-9]/.test(char);
    }
  }
  return false;
}

/**
 * Calcule la nouvelle position du curseur après formatage
 * @param oldValue - L'ancienne valeur formatée
 * @param newValue - La nouvelle valeur formatée
 * @param oldCursorPosition - L'ancienne position du curseur
 * @param maskType - Le type de masque
 * @returns La nouvelle position du curseur
 */
export function getNewCursorPosition(
  oldValue: string,
  newValue: string,
  oldCursorPosition: number,
  maskType: 'phone' | 'postalCodeCA'
): number {
  // Obtenir les valeurs non formatées
  const unmaskedOld = maskType === 'phone' ? unmaskPhone(oldValue) : unmaskPostalCode(oldValue);
  const unmaskedNew = maskType === 'phone' ? unmaskPhone(newValue) : unmaskPostalCode(newValue);
  
  // Compter combien de caractères non formatés étaient avant le curseur dans l'ancienne valeur
  let unmaskedCharsBeforeCursor = 0;
  for (let i = 0; i < oldCursorPosition && i < oldValue.length; i++) {
    const char = oldValue[i];
    if (maskType === 'phone' && /[0-9]/.test(char)) {
      unmaskedCharsBeforeCursor++;
    } else if (maskType === 'postalCodeCA' && /[A-Za-z0-9]/.test(char)) {
      unmaskedCharsBeforeCursor++;
    }
  }
  
  // Déterminer si on a ajouté ou supprimé des caractères
  const lengthDiff = unmaskedNew.length - unmaskedOld.length;
  
  // Calculer la position cible dans la valeur non formatée
  // Si on a ajouté des caractères (saisie normale), avancer le curseur
  // Si on a supprimé des caractères (backspace/delete), garder la position relative
  // Si la longueur est la même mais que la valeur a changé, c'est un remplacement (texte sélectionné)
  let targetUnmaskedPosition = unmaskedCharsBeforeCursor;
  if (lengthDiff > 0) {
    // On a ajouté un ou plusieurs caractères, avancer le curseur
    targetUnmaskedPosition = unmaskedCharsBeforeCursor + lengthDiff;
  } else if (lengthDiff === 0 && unmaskedOld !== unmaskedNew) {
    // La longueur est la même mais la valeur a changé : remplacement de texte sélectionné
    // Dans ce cas, on place le curseur après le caractère inséré
    targetUnmaskedPosition = unmaskedCharsBeforeCursor + 1;
  } else if (lengthDiff < 0) {
    // On a supprimé des caractères, garder la position relative
    targetUnmaskedPosition = Math.max(0, unmaskedCharsBeforeCursor + lengthDiff);
  }
  
  // Limiter la position cible à la longueur de la nouvelle valeur
  targetUnmaskedPosition = Math.min(targetUnmaskedPosition, unmaskedNew.length);
  
  // Trouver la position correspondante dans la nouvelle valeur formatée
  let newPosition = 0;
  let unmaskedCount = 0;
  
  for (let i = 0; i < newValue.length; i++) {
    const char = newValue[i];
    const isUnmaskedChar = (maskType === 'phone' && /[0-9]/.test(char)) || 
                           (maskType === 'postalCodeCA' && /[A-Za-z0-9]/.test(char));
    
    if (isUnmaskedChar) {
      unmaskedCount++;
      // Placer le curseur après ce caractère si on a atteint la position cible
      if (unmaskedCount <= targetUnmaskedPosition) {
        newPosition = i + 1;
      }
    } else {
      // Pour les caractères de formatage, avancer aussi si on n'a pas encore atteint la position cible
      if (unmaskedCount < targetUnmaskedPosition) {
        newPosition = i + 1;
      }
    }
  }
  
  // Si on est à la fin, placer le curseur après le dernier caractère
  if (unmaskedCount < targetUnmaskedPosition) {
    newPosition = newValue.length;
  }
  
  return newPosition;
}
